import React, { useCallback, useEffect, useState } from 'react';

import { Icon, RotateIcon, VisuallyHidden } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import IconAmex from 'components/icons/amex';
import IconDiscover from 'components/icons/discover';
import IconMastercard from 'components/icons/mastercard';
import IconVisa from 'components/icons/visa';
import PaymentMethodOption from 'components/payment-method-option';
import { MethodContainer } from 'components/payment-method-option/styled';
import UserVerificationModal from 'components/user-verification-modal';
import useUserHasToVerify from 'hooks/use-user-has-to-verify';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { shouldReVault } from 'utils/payment';
import { relativeRoutes } from 'utils/routing';

import { ICollapsible, IHasBorder, IPaymentMethodProps } from './types';

const FormContainer = styled.div<ICollapsible & IHasBorder>`
  background-color: ${Styles.color.white};
  border: ${p => (p.hasBorder ? `1px solid ${Styles.color.grey.four}` : undefined)};
  border-radius: ${Styles.borderRadius};
  overflow: hidden;
  cursor: pointer;
  margin: 0 0 1rem 0;
  z-index: ${Styles.zIndex.overlay};
  position: relative;
  label {
    display: ${p => (p.collapsed ? 'none' : 'block')};
    &:first-of-type {
      display: block;
    }
  }
`;

const FormContainerWrapper = styled.div`
  position: relative;
  height: 3.375rem;
`;

const AddNewCardLink = styled.button`
  border: transparent;
  color: ${Styles.color.black};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${Styles.fontFamily.body};
  font-size: 1rem;
  font-weight: normal;
  height: 54px;
  user-select: none;
  padding: 0 0.875rem 0 0.875rem;
  width: 100%;
  text-decoration: none;
  transition: opacity 2s ease-out;
  background: #fff;
  transition: 0.15s;
  text-align: left;

  &:hover {
    background-color: ${Styles.color.grey.five};
  }
`;

const DisplayToggle = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 1.15rem;
  pointer-events: none;
  a {
    display: none;
  }
`;

const CcWrapper = styled.div`
  display: flex;
  align-content: center;

  svg {
    width: 1.5rem;
    fill: ${Styles.color.black};
    margin-left: 5px;
    height: 15px;
  }
`;

const NoPaymentMethodsText = styled.p`
  font-size: 1rem;
`;

const SelectCcDropdownText = styled(MethodContainer)`
  padding: 0 2rem 0 0.825rem;
  color: ${Styles.color.black};
  font-size: 1rem;
`;

const DirectPayOnboarding = styled.div`
  background-color: ${Styles.color.grey.five};
  padding: 1rem;
  border-radius: ${Styles.borderRadius};
  margin-bottom: 1rem;
  h3 {
    margin: 0;
    font-size: 1.1rem;
  }
  p {
    margin: 0;
    color: ${Styles.color.black};
  }
`;

const AddNewCard = ({
  handleAddNewCardClick,
  text,
}: {
  handleAddNewCardClick: (e: React.MouseEvent) => void;
  text: string;
}) => (
  <AddNewCardLink data-testid="add-new-cc-link" onClick={handleAddNewCardClick}>
    {text}
    <CcWrapper>
      <IconVisa />
      <IconAmex />
      <IconMastercard />
      <IconDiscover />
    </CcWrapper>
  </AddNewCardLink>
);

function PaymentMethod({
  methods,
  selectedMethodId,
  handleSelect,
  hasBorder,
  showCollapsibleMenu = true,
  checkoutPaymentMethodNeedRevault,
  enableDirectPay,
}: IPaymentMethodProps) {
  const { formatMessage } = useIntl();
  // If thDirect is enabled & the selected checkout payment method is not valid for thDirect pay
  // Show 'Select a Credit Card' instead of the selected payment method in the dropdown
  const selectedMethod = checkoutPaymentMethodNeedRevault
    ? undefined
    : methods.find(method => (method.accountIdentifier ?? method.fdAccountId) === selectedMethodId);
  const nonSelectedMethods = checkoutPaymentMethodNeedRevault
    ? methods
    : methods.filter(
        method => (method.accountIdentifier ?? method.fdAccountId) !== selectedMethodId
      );
  const [collapsed, setCollapsed] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const navigate = useNavigate();
  const enableUnverifiedSavedPayments = useFlag(LaunchDarklyFlag.ENABLE_UNVERIFIED_SAVED_PAYMENTS);
  const userHasToVerify = useUserHasToVerify(enableUnverifiedSavedPayments);

  const enableDoubleVaultingFromPaymentMethods = useFlag(
    LaunchDarklyFlag.ENABLE_DOUBLE_VAULTING_FROM_PAYMENT_METHODS
  );

  // if the selected method id changes externally
  // reset collapsed to true
  useEffect(() => {
    if (!collapsed && selectedMethodId) {
      setCollapsed(true);
    }
  }, [selectedMethodId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = useCallback(
    clickedAccountIdentifier => {
      const needReVault = shouldReVault({
        accountIdentifier: clickedAccountIdentifier,
        enableDoubleVaultingFromPaymentMethods,
        methods,
      });
      if (needReVault) {
        // Open the add new CC modal. If a user adds a CC, delete the old one with its accountIdentifier
        navigate(relativeRoutes.addCard, { state: { accountToDelete: clickedAccountIdentifier } });
      } else {
        handleSelect(clickedAccountIdentifier);
      }
      setCollapsed(true);
    },
    [enableDoubleVaultingFromPaymentMethods, handleSelect, methods, navigate]
  );

  const handleCollapseClick = useCallback(() => {
    if (showCollapsibleMenu) {
      setCollapsed(currentCollapsed => !currentCollapsed);
    }
  }, [showCollapsibleMenu]);
  const handleAddNewCardClick = useCallback(
    e => {
      e.preventDefault();
      setCollapsed(true);
      if (userHasToVerify) {
        setShowVerificationModal(true);
      } else {
        navigate(relativeRoutes.addCard);
      }
    },
    [userHasToVerify, navigate]
  );

  return (
    <div data-testid="payment-methods">
      {enableDirectPay && (
        <DirectPayOnboarding>
          <h3>{formatMessage({ id: 'thDirectPayOnboardingTitle' })}</h3>
          <p>{formatMessage({ id: 'thDirectPayOnboardingText' })}</p>
        </DirectPayOnboarding>
      )}

      {!methods.length && (
        <NoPaymentMethodsText data-testid="no-payment-method-text">
          {formatMessage({ id: 'noPaymentMethodSaved' })}
        </NoPaymentMethodsText>
      )}

      <FormContainerWrapper>
        <FormContainer collapsed={collapsed} hasBorder={hasBorder}>
          {showCollapsibleMenu && !!methods.length && (
            <DisplayToggle
              onClick={handleCollapseClick}
              aria-expanded={!collapsed}
              data-testid="display-toggle-payment-method"
            >
              <RotateIcon active={!collapsed}>
                <Icon icon="select" color="icon-form-input-action" width="24px" aria-hidden />
              </RotateIcon>
            </DisplayToggle>
          )}
          {methods.length ? (
            checkoutPaymentMethodNeedRevault ? (
              <SelectCcDropdownText onClick={handleCollapseClick} hideBorder={collapsed}>
                {formatMessage({ id: 'selectPaymentMethod' })}
              </SelectCcDropdownText>
            ) : (
              selectedMethod && (
                <>
                  <PaymentMethodOption
                    method={selectedMethod}
                    onClick={handleCollapseClick}
                    disableHover={true}
                    hideBorder={collapsed}
                    ariaLabelledby="payment-method-tim-card"
                  />
                  <VisuallyHidden id="payment-method-tim-card">
                    {formatMessage({ id: 'yourSelectedPaymentMethodIsTimCard' })}
                  </VisuallyHidden>
                </>
              )
            )
          ) : (
            <AddNewCard
              handleAddNewCardClick={handleAddNewCardClick}
              text={formatMessage({ id: 'addNewPaymentMethod' })}
            />
          )}

          {!collapsed && (
            <>
              {nonSelectedMethods.map(method => (
                <PaymentMethodOption
                  key={method.accountIdentifier ?? method.fdAccountId ?? ''}
                  method={method}
                  onClick={() => {
                    if (showCollapsibleMenu) {
                      handleClick(method.accountIdentifier ?? method.fdAccountId ?? '');
                    }
                  }}
                  disableHover={!showCollapsibleMenu}
                  revaultRequired={shouldReVault({
                    accountIdentifier: method.accountIdentifier ?? method.fdAccountId ?? '',
                    enableDoubleVaultingFromPaymentMethods,
                    methods,
                  })}
                />
              ))}
              {showCollapsibleMenu && (
                <AddNewCard
                  handleAddNewCardClick={handleAddNewCardClick}
                  text={formatMessage({ id: 'addNewPaymentMethod' })}
                />
              )}
            </>
          )}
        </FormContainer>
      </FormContainerWrapper>
      {showVerificationModal && (
        <UserVerificationModal onDismiss={() => setShowVerificationModal(false)} />
      )}
    </div>
  );
}

export default PaymentMethod;
